import { useState, useMemo } from 'react'
import { useNavigate } from 'react-router-dom'
import { useUX } from '@/ux'
import { getIntents } from './intents'
import { executeAction } from './actions'
import { Intent } from './types'
import { Avatar } from './Avatar'
import { VideoPlayer } from './VideoPlayer'
import { useTranslation } from '@/i18n'

interface AssistantDrawerProps {
  isOpen: boolean
  onClose: () => void
}

export function AssistantDrawer({ isOpen, onClose }: AssistantDrawerProps) {
  const [selectedIntent, setSelectedIntent] = useState<Intent | null>(null)
  const { setPersona, locale } = useUX()
  const { t } = useTranslation()
  const navigate = useNavigate()

  // Get translated intents based on current locale
  const INTENTS = useMemo(() => getIntents(locale), [locale])

  const handleIntentClick = (intent: Intent) => {
    setSelectedIntent(intent)

    // Execute action
    executeAction(intent.action, {
      setPersona,
      navigate,
    })
  }

  const handleBack = () => {
    setSelectedIntent(null)
  }

  return (
    <>
      {/* Backdrop */}
      {isOpen && (
        <div
          className="fixed inset-0 bg-black/30 backdrop-blur-sm z-40 transition-opacity dark:bg-black/60"
          onClick={onClose}
          role="presentation"
          aria-hidden="true"
        />
      )}

      {/* Drawer */}
      <aside
        className={`fixed right-0 top-0 h-full w-[70%] sm:w-[75%] md:w-[480px] bg-white shadow-2xl z-50 transform transition-transform duration-300 ease-in-out dark:bg-gray-900 ${
          isOpen ? 'translate-x-0' : 'translate-x-full'
        }`}
        role="dialog"
        aria-modal="true"
        aria-label="Portfolio Assistant"
        aria-hidden={!isOpen}
      >
        <div className="flex flex-col h-full">
          {/* Header */}
          <div className="flex items-center justify-between p-4 border-b border-gray-200 dark:border-gray-700">
            <div className="flex items-center gap-3">
              <Avatar size="sm" />
              <div>
                <h2 className="font-bold text-lg dark:text-gray-100">{t('assistant.title')}</h2>
                <p className="text-xs text-gray-500 dark:text-gray-400">
                  {t('assistant.subtitle')}
                </p>
              </div>
            </div>
            <button
              onClick={onClose}
              className="p-2 hover:bg-gray-100 rounded-lg transition dark:hover:bg-gray-800"
              aria-label={t('assistant.close')}
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M6 18L18 6M6 6l12 12"
                />
              </svg>
            </button>
          </div>

          {/* Content */}
          <div className="flex-1 overflow-y-auto p-4">
            {!selectedIntent ? (
              // Intent selection
              <div>
                <p className="text-gray-600 mb-6 dark:text-gray-400">
                  {t('assistant.chooseIntent')}
                </p>
                <div className="space-y-3">
                  {INTENTS.map((intent) => (
                    <button
                      key={intent.id}
                      type="button"
                      onClick={() => handleIntentClick(intent)}
                      className="w-full text-left p-4 bg-gray-50 hover:bg-blue-50 border border-gray-200 hover:border-blue-300 rounded-lg transition dark:bg-gray-800 dark:hover:bg-blue-950/30 dark:border-gray-700 dark:hover:border-blue-700"
                      aria-label={intent.label}
                    >
                      <div className="flex items-start gap-3">
                        <span className="text-2xl">{intent.icon}</span>
                        <div className="flex-1">
                          <h3 className="font-semibold mb-1 dark:text-gray-100">{intent.label}</h3>
                          <p className="text-sm text-gray-600 dark:text-gray-400">
                            {intent.description}
                          </p>
                        </div>
                      </div>
                    </button>
                  ))}
                </div>
              </div>
            ) : (
              // Intent response
              <div>
                <button
                  type="button"
                  onClick={handleBack}
                  className="mb-4 text-sm text-blue-600 hover:underline flex items-center gap-1 dark:text-blue-400"
                  aria-label={t('assistant.backToMenu')}
                >
                  {t('assistant.backToMenu')}
                </button>

                <div className="mb-4 flex items-center gap-3">
                  <span className="text-3xl">{selectedIntent.icon}</span>
                  <h3 className="text-xl font-bold dark:text-gray-100">{selectedIntent.label}</h3>
                </div>

                <div className="bg-blue-50 border-l-4 border-blue-500 p-4 rounded-r-lg mb-6 dark:bg-blue-950/20 dark:border-blue-400">
                  <p className="text-gray-700 dark:text-gray-300">{selectedIntent.response}</p>
                </div>

                {/* Video player or fallback */}
                <VideoPlayer
                  src={selectedIntent.videoSrc}
                  fallbackMessage={t('assistant.actionExecuted')}
                />
              </div>
            )}
          </div>

          {/* Footer */}
          <div className="p-4 border-t border-gray-200 bg-gray-50 dark:border-gray-700 dark:bg-gray-800">
            <p className="text-xs text-gray-500 text-center dark:text-gray-400">
              {t('assistant.footer')}
            </p>
          </div>
        </div>
      </aside>
    </>
  )
}
